import axios from 'axios';
import { 
    updateResult, notifyResultError,
    updateClicked, notifyClickedError,
    updateQuiz, notifyQuizError,
    updateTrainingStatus, notifyTrainingStatusError
} from '../slice/TrainingSlice';
import { refreshAuth } from '../slice/UserSlice';

const url = process.env.REACT_APP_API_URL;

export function getClicked(auth){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'
        };

        //200 OK = map of training links the user has opened (gates quiz visibility)
        return axios.get( `${url}/clickedmodules`, { headers: header } )
            .then(res => {
                if (res.status === 200){
                    dispatch(updateClicked(res.data))
                }
            })
            .catch(function (error) {
                if (error.name === 'AxiosError'){
                    if (error.response.status === 401){
                        window.sessionStorage.removeItem('authDetails')
                        dispatch(refreshAuth());
                    }
                    dispatch(notifyClickedError(error.response.data.error ?? 'Unknown error'));
                }
                else
                    dispatch(notifyClickedError(error.error));
                console.log(error);
            });
    }
}

export function recordClick(auth, module){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'
        };

        let details = {
            name: module
        };

        //200 OK = updated map of training links the user has opened
        return axios.post( `${url}/clickedmodules`, JSON.stringify(details), { headers: header } )
            .then(res => {
                if (res.status === 200){
                    dispatch(updateClicked(res.data))
                }
            })
            .catch(function (error) {
                if (error.name === 'AxiosError'){
                    if (error.response.status === 401){
                        window.sessionStorage.removeItem('authDetails')
                        dispatch(refreshAuth());
                    }
                    dispatch(notifyClickedError(error.response.data.error ?? 'Unknown error'));
                }
                else
                    dispatch(notifyClickedError(error.error));
                console.log(error);
            });
    }
}

// getTrainingStatus fetches the watermark-aware training breakdown from the API
// (which sources it from the User Management service, the sole source of truth
// for completion). The SPA uses it to pre-tick training that is already complete
// and still valid; on any failure nothing is pre-ticked and the training shows
// fresh.
export function getTrainingStatus(auth, merchantId){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'
        };

        // Gate on the store the Merchant SPA launched training for (passed through
        // the URL), not the write-once merchant stamped on the registration record.
        const query = merchantId ? `?merchantId=${encodeURIComponent(merchantId)}` : '';

        //200 OK = { complianceRequired, complianceRegion, assistedApplyRequired }
        return axios.get( `${url}/trainingstatus${query}`, { headers: header } )
            .then(res => {
                if (res.status === 200){
                    dispatch(updateTrainingStatus(res.data))
                }
            })
            .catch(function (error) {
                if (error.name === 'AxiosError'){
                    if (error.response && error.response.status === 401){
                        window.sessionStorage.removeItem('authDetails')
                        dispatch(refreshAuth());
                    }
                    dispatch(notifyTrainingStatusError(error.response && error.response.data ? error.response.data.error : 'Unknown error'));
                }
                else
                    dispatch(notifyTrainingStatusError(error.error));
                console.log(error);
            });
    }
}

export function getQuiz(auth, module){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'
        };

        //200 OK = quiz pool for the module (questions only, solution stripped server-side)
        return axios.get( `${url}/quiz/${module}`, { headers: header } )
            .then(res => {
                if (res.status === 200){
                    dispatch(updateQuiz(res.data))
                }
            })
            .catch(function (error) {
                if (error.name === 'AxiosError'){
                    if (error.response.status === 401){
                        window.sessionStorage.removeItem('authDetails')
                        dispatch(refreshAuth());
                    }
                    dispatch(notifyQuizError(error.response.data.error ?? 'Unknown error'));
                }
                else
                    dispatch(notifyQuizError(error.error));
                console.log(error);
            });
    }
}

// fetchQuiz fetches the quiz pool for a single module and RESOLVES with the data
// instead of writing to the shared redux `quiz` singleton. This lets several
// module sections load their own pools on one page without clobbering each other.
export function fetchQuiz(auth, module){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'
        };

        return axios.get( `${url}/quiz/${module}`, { headers: header } )
            .then(res => (res.status === 200 ? res.data : null))
            .catch(function (error) {
                if (error.name === 'AxiosError' && error.response && error.response.status === 401){
                    window.sessionStorage.removeItem('authDetails')
                    dispatch(refreshAuth());
                }
                console.log(error);
                throw error;
            });
    }
}

// submitAttempt submits a single module attempt and RESOLVES with the result
// (score) instead of writing to the shared redux `result` singleton, so each
// module section on a combined page tracks its own pass/fail independently.
export function submitAttempt(auth, details){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'
        };

        return axios.post( `${url}/submit`, JSON.stringify(details), { headers: header } )
            .then(res => (res.status === 200 ? res.data : null))
            .catch(function (error) {
                if (error.name === 'AxiosError' && error.response && error.response.status === 401){
                    window.sessionStorage.removeItem('authDetails')
                    dispatch(refreshAuth());
                }
                console.log(error);
                throw error;
            });
    }
}

export function submit(auth, details){
    return function (dispatch) {
        let header = {
            'Authorization': `Bearer ${auth}`,
            'Content-Type': 'text/plain'            
        };

        // let details = {
        // 1: a,
        // 2: b,
        // 3: {a,b,d}
        // }

        return axios.post( `${url}/submit`, JSON.stringify(details), { headers: header } )
            .then(res => { 
                if (res.status === 200){
                    dispatch(updateResult(res.data))
                    console.log(res.data)
                }
            })
            .catch(function (error) {
                if (error.name === 'AxiosError'){      
                    if (error.response.status === 401){
                        window.sessionStorage.removeItem('authDetails')
                        dispatch(refreshAuth());         
                    }               
                    dispatch(notifyResultError(error.response.data.error ?? 'Unknown error'));
                }
                else
                    dispatch(notifyResultError(error.error));
                console.log(error);
            });
    }
}